<style lang="less" scoped>
  @import "../../../style/theme.less";
</style>

<template>
  <div>
    <a-alert
      v-for="(error, index) in errors"
      :key="index"
      :message="error"
      type="info"
      show-icon style="margin-bottom: 16px;"
    />

    <a-row :gutter="[16, 16]" v-for="(condition, index) in conditionDisplay" :key="index">
      <a-col :span="condition.inputType === 'personalityTrait' ? 4 : 7">
        <a-select
          @change="inputType => updateInput(index, { inputType })"
          style="width: 100%"
          placeholder="Input Type"
          :value="condition.inputType"
        >
          <a-select-option :value="undefined">none</a-select-option>

          <a-select-option
            v-for="type in Object.keys(constants.conditions.types)"
            :key="type"
            :value="type"
          >
            {{ constants.conditions.typeDescriptors[type] }}
          </a-select-option>
        </a-select>
      </a-col>

      <a-col :span="3" v-if="condition.inputType === 'personalityTrait'">
        <a-select
          @change="inputId => updateInput(index, { inputId })"
          style="width: 100%"
          placeholder="Personality Trait"
          :value="condition.inputId"
        >
          <a-select-option :value="null">none</a-select-option>

          <a-select-option
            v-for="trait in personalityTraits"
            :key="trait.id"
            :value="trait.id"
          >
            {{ trait.descriptor }}
          </a-select-option>
        </a-select>
      </a-col>

      <a-col :span="2" v-if="condition.inputType === 'personalityTrait'">
        <a-input-number
          :min="1"
          :max="5"
          :value="condition.inputIndex"
          style="width: 100%"
          @change="inputIndex => updateInput(index, { inputIndex })"
          placeholder="Index"
        />
      </a-col>

      <a-col :span="condition.inputType === 'personalityTrait' ? 3 : 5">
        <a-select
          @change="operator => updateInput(index, { operator })"
          style="width: 100%"
          placeholder="Operator"
          :value="condition.operator"
        >
          <a-select-option :value="null">none</a-select-option>

          <a-select-option
            v-for="operator in operators"
            :key="operator.key"
            :value="operator.key"
          >
            {{ operator.descriptor }}
          </a-select-option>
        </a-select>
      </a-col>

      <a-col :span="condition.comparatorType === 'personalityTrait' ? 4 : 7">
        <a-select
          @change="comparatorType => updateInput(index, { comparatorType })"
          style="width: 100%"
          placeholder="Type"
          :value="condition.comparatorType"
        >
          <a-select-option :value="undefined">none</a-select-option>

          <a-select-option value="literal">Literal</a-select-option>

          <a-select-option
            v-for="type in Object.keys(constants.conditions.types)"
            :key="type"
            :value="type"
          >
            {{ constants.conditions.typeDescriptors[type] }}
          </a-select-option>
        </a-select>
      </a-col>

      <a-col :span="3" v-if="condition.comparatorType === 'personalityTrait'">
        <a-select
          @change="comparatorId => updateInput(index, { comparatorId })"
          style="width: 100%"
          placeholder="Personality Trait"
          :value="condition.comparatorId"
        >
          <a-select-option :value="null">none</a-select-option>

          <a-select-option
            v-for="trait in personalityTraits"
            :key="trait.id"
            :value="trait.id"
          >
            {{ trait.descriptor }}
          </a-select-option>
        </a-select>
      </a-col>

      <a-col :span="2" v-if="condition.comparatorType === 'personalityTrait'">
        <a-input-number
          :min="1"
          :max="5"
          :value="condition.comparatorIndex"
          @change="comparatorIndex => updateInput(index, { comparatorIndex })"
          style="width: 100%"
          placeholder="Index"
        />
      </a-col>

      <a-col :span="2" v-if="condition.comparatorType === 'literal'">
        <a-select
          v-if="condition.inputType === 'gender'"
          @change="comparatorValue => updateInput(index, { comparatorValue })"
          style="width: 100%"
          placeholder="Gender"
          :value="condition.comparatorValue"
        >
          <a-select-option :value="null">none</a-select-option>

          <a-select-option
            v-for="gender in Object.keys(constants.genders.genders)"
            :key="gender"
            :value="gender"
          >
            {{ constants.genders.genderDescriptors[gender] }}
          </a-select-option>
        </a-select>

        <a-input
          v-else
          :value="condition.comparatorValue"
          @change="({ srcElement: { value: comparatorValue }}) => updateInput(index, { comparatorValue })"
          style="width: 100%"
          placeholder="Value"
        />
      </a-col>

      <a-col :span="['personalityTrait', 'literal'].includes(condition.comparatorType) ? 2 : 4">
        <a-input-number
          :value="condition.comparatorCorrection"
          @change="comparatorCorrection => updateInput(index, { comparatorCorrection })"
          style="width: 100%"
          placeholder="Correction"
        />
      </a-col>

      <a-col :span="1">
        <a-button
          v-if="Object.values(condition).filter(v => !!v).length > 0"
          type="primary"
          shape="circle"
          icon="delete"
          @click="() => removeInput(index)"
        />
      </a-col>
    </a-row>
  </div>
</template>

<script>

import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  name: 'admin.conditions-editor',

  data: () => ({
    conditionInputs: [],

    errors: [],
  }),

  props: ['conditions',],

  mounted() {
    this.reset();

    return Promise.all([
      this.listPersonalityTraits(),
      this.listConstants(),
    ]);
  },

  computed: {
    ...mapState('admin', ['personalityTraits', 'constants',]),

    conditionDisplay() {
      return [
        ...this.conditionInputs.filter(c => Object.values(c).filter(v => !!v).length > 0),
        {},
      ];
    },

    operators() {
      const operatorKeys = Object.keys(this.constants.conditions.operators);
      const inclusiveAllowedOperators = this.constants.conditions.inclusiveAllowedOperators;
      const operatorDescriptors = this.constants.conditions.operatorDescriptors;

      return operatorKeys.reduce((acc, key) => {
        acc.push({
          key,
          descriptor: operatorDescriptors[key],
        });

        if (inclusiveAllowedOperators.includes(key)) {
          acc.push({
            key: `${key}Incl`,
            descriptor: `${operatorDescriptors[key]} or Equal`,
          });
        }

        return acc;
      }, []);
    },
  },

  methods: {
    ...mapActions('admin', ['listPersonalityTraits', 'listConstants',]),
    ...mapGetters('admin', ['resolvePersonalityTrait',]),

    parseOperator: operator => ({ operator: operator.split('Incl')[0], inclusive: operator.endsWith('Incl'), }),

    formatConditions(conditions) {
      return Object.keys(conditions).reduce((acc, type) => {

        const personalityTrait = this.resolvePersonalityTrait()(type);

        const inputType = personalityTrait.id ? 'personalityTrait' : type;
        const inputId = personalityTrait.id;
        const inputIndex = personalityTrait.index == null ? undefined : personalityTrait.index + 1;

        const condition = conditions[type];
        Object.keys(condition).forEach(operator => {
          const {
            type: comparatorType,
            value: comparatorValue,
            inclusive,
            correction,
          } = condition[operator];

          const comparatorCorrection = correction ? (correction.add || 0) - (correction.subtract || 0): null;
          let comparatorId, comparatorIndex;
          if (comparatorType === this.constants.conditions.types.personalityTrait) {
            const personalityTrait = this.resolvePersonalityTrait()(comparatorValue);
            comparatorId = personalityTrait.id;
            comparatorIndex = personalityTrait.index ? personalityTrait.index + 1 : null;
          }

          acc.push({
            inputType,
            inputId,
            inputIndex,
            operator: `${operator}${inclusive === false ? '' : 'Incl'}`,
            comparatorValue,
            comparatorType,
            comparatorId,
            comparatorIndex,
            comparatorCorrection,
          });
        });

        return acc;
      }, []);
    },

    exportConditions() {
      return this.conditionInputs.reduce((acc, input) => {
        const {
          comparatorCorrection,
          comparatorId,
          comparatorIndex,
          comparatorType,
          comparatorValue,
          inputId,
          inputIndex,
          inputType,
        } = input;

        let { operator, inclusive, } = this.parseOperator(input.operator);

        const key = inputType === 'personalityTrait' ? (inputId + (inputIndex ? `:${inputIndex - 1}` : '')) : inputType;

        let value;
        if (comparatorType === this.constants.conditions.types.literal) {
          value = comparatorValue;
        } else if (comparatorType === this.constants.conditions.types.personalityTrait) {
          value = comparatorId + (comparatorIndex ? `:${comparatorIndex - 1}` : '');
        }

        let correction;
        if (comparatorCorrection) {
          correction = comparatorCorrection < 0 ? { subtract: -comparatorCorrection, } : { add: comparatorCorrection, };
        }

        const condition = {
          type: comparatorType,
          value,
          inclusive,
          correction,
        };

        acc[key] = acc[key] || {};
        acc[key][operator] = condition;

        return acc;
      }, {});
    },

    validateConditionInputs() {
      this.errors = [];
      this.conditionInputs.forEach((condition, index) => {
        const error = message => this.errors.push(`Condition ${index+1} has ${message}`);

        if (!condition.inputType) {
          return error('missing input type');
        }
        const requiredInputs = {
          personalityTrait: ['inputId',],
          gender: [],
          age: [],
        }[condition.inputType];

        if (requiredInputs.some(key => !condition[key])) {
          return error('missing input information');
        }

        const allowedOperators = Object.keys(this.constants.conditions.operators);

        const { operator, } = this.parseOperator(condition.operator);

        if (!condition.operator || !allowedOperators.includes(operator)) {
          return error('invalid or missing operator');
        }

        if (!condition.comparatorType) {
          return error('missing comparison type');
        }
        const requiredComparators = {
          literal: ['comparatorValue',],
          personalityTrait: ['comparatorId',],
          gender: [],
          age: [],
        }[condition.comparatorType];

        if (requiredComparators.some(key => !condition[key])) {
          return error('missing comparison information');
        }

        if (condition.comparatorCorrection && isNaN(parseInt(condition.comparatorCorrection))) {
          return error('invalid correction');
        }
      });

      if (this.errors.length === 0) {
        this.$emit('change', this.exportConditions());
      }
    },

    updateInput(index, data) {
      this.$set(this.conditionInputs, index, { ...this.conditionInputs[index], ...data, });
      this.conditionInputs = this.conditionInputs.filter(c => Object.values(c).filter(v => !!v).length > 0);

      this.validateConditionInputs();
    },

    removeInput(index) {
      this.conditionInputs.splice(index, 1);
      this.validateConditionInputs();
    },

    reset() {
      return this.conditionInputs = this.formatConditions(this.conditions);
    },
  },
};
</script>
